import type { ReactNode } from 'react';

function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|\[(\d+(?:,\s*\d+)*)\]|(https?:\/\/[^\s<>()]+[^\s<>().,;:])/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${key}-${i++}`;
    if (m[1] !== undefined) {
      out.push(<strong key={k}>{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      out.push(<a key={k} href={m[3]} target="_blank" rel="noopener noreferrer">{m[2]}</a>);
    } else if (m[5] !== undefined) {
      out.push(<a key={k} href={m[5]} target="_blank" rel="noopener noreferrer">{m[5]}</a>);
    } else {
      const nums = m[4].split(',').map((n) => n.trim());
      out.push(
        <sup key={k} className="guide-cite">
          [{nums.map((n, j) => (
            <span key={n}>{j > 0 && ', '}<a href={`#source-${n}`}>{n}</a></span>
          ))}]
        </sup>,
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function Markdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let para: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let inSources = false;
  let table: string[][] = [];
  let quote: string[] = [];

  const flushPara = () => {
    if (para.length) blocks.push(<p key={`p${blocks.length}`}>{inline(para.join(' '), `p${blocks.length}`)}</p>);
    para = [];
  };
  const flushTable = () => {
    if (!table.length) return;
    const k = `t${blocks.length}`;
    const [head, ...body] = table;
    const hasHead = head.some((c) => c !== '');
    blocks.push(
      <table key={k}>
        {hasHead && <thead><tr>{head.map((c, i) => <th key={i}>{inline(c, `${k}-h${i}`)}</th>)}</tr></thead>}
        <tbody>{body.map((r, j) => <tr key={j}>{r.map((c, i) => <td key={i}>{inline(c, `${k}-${j}-${i}`)}</td>)}</tr>)}</tbody>
      </table>,
    );
    table = [];
  };
  const flushQuote = () => {
    if (!quote.length) return;
    const k = `q${blocks.length}`;
    blocks.push(<blockquote key={k}>{quote.map((q, i) => <p key={i}>{inline(q, `${k}-${i}`)}</p>)}</blockquote>);
    quote = [];
  };
  const flushList = () => {
    if (!list) return;
    const k = `l${blocks.length}`;
    const items = list.items.map((it, i) => (
      <li key={i} id={list!.ordered && inSources ? `source-${i + 1}` : undefined}>{inline(it, `${k}-${i}`)}</li>
    ));
    blocks.push(list.ordered ? <ol key={k}>{items}</ol> : <ul key={k}>{items}</ul>);
    list = null;
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.startsWith('|')) flushTable();
    if (!line.startsWith('>')) flushQuote();
    if (line.startsWith('|')) {
      flushPara();
      flushList();
      const cells = line.replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
      if (!cells.every((c) => /^:?-+:?$/.test(c))) table.push(cells);
    } else if (line.startsWith('>')) {
      flushPara();
      flushList();
      const q = line.replace(/^>\s?/, '');
      if (q) quote.push(q);
    } else if (line === '---') {
      flushPara();
      flushList();
      blocks.push(<hr key={`r${blocks.length}`} />);
    } else if (line.startsWith('### ')) {
      flushPara();
      flushList();
      blocks.push(<h3 key={`h${blocks.length}`}>{line.slice(4).trim()}</h3>);
    } else if (line.startsWith('# ')) {
      flushPara();
      flushList();
      blocks.push(<h2 key={`h${blocks.length}`}>{line.slice(2).trim()}</h2>);
    } else if (line.startsWith('## ')) {
      flushPara();
      flushList();
      const title = line.slice(3).trim();
      inSources = title.toLowerCase() === 'sources';
      blocks.push(<h2 key={`h${blocks.length}`}>{title}</h2>);
    } else if (/^\s*- /.test(line)) {
      flushPara();
      if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] }; }
      list.items.push(line.replace(/^\s*- /, ''));
    } else if (/^\s*\d+\. /.test(line)) {
      flushPara();
      if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] }; }
      list.items.push(line.replace(/^\s*\d+\. /, ''));
    } else if (line.trim() === '') {
      flushPara();
      flushList();
    } else if (list && /^\s+/.test(raw)) {
      list.items[list.items.length - 1] += ' ' + line.trim();
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  flushTable();
  flushQuote();
  return <div className="guide-body">{blocks}</div>;
}
